import React, { useState } from "react";
import styles from "./VendorsPage.module.css";
import { Vendor } from "./types";
import { mockVendors } from "./mockData";

export default function VendorsPage() {
  const [vendors, setVendors] = useState<Vendor[]>(mockVendors);
  const [searchQuery, setSearchQuery] = useState("");
  const [showDrawer, setShowDrawer] = useState(false);
  const [editingVendor, setEditingVendor] = useState<Vendor | null>(null);
  const [isCreating, setIsCreating] = useState(false);

  const filteredVendors = vendors.filter(vendor => 
    vendor.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    vendor.skills.some(skill => skill.toLowerCase().includes(searchQuery.toLowerCase())) ||
    vendor.businessModels.some(model => model.toLowerCase().includes(searchQuery.toLowerCase()))
  );

  const handleCreate = () => {
    setIsCreating(true);
    setEditingVendor({
      id: "",
      name: "",
      skills: [],
      businessModels: [],
      rideAssignment: "Manual",
      assignedDrivers: 0,
      status: "Active"
    });
    setShowDrawer(true);
  };

  const handleEdit = (vendor: Vendor) => {
    setIsCreating(false);
    setEditingVendor({ ...vendor });
    setShowDrawer(true);
  };

  const handleCloseDrawer = () => {
    setShowDrawer(false);
    setEditingVendor(null);
    setIsCreating(false);
  };

  const handleSave = () => {
    if (!editingVendor) return;

    if (isCreating) {
      const newVendor = {
        ...editingVendor,
        id: Date.now().toString()
      };
      setVendors([...vendors, newVendor]);
    } else {
      setVendors(vendors.map(v => 
        v.id === editingVendor.id ? editingVendor : v
      ));
    }

    handleCloseDrawer();
  };

  const handleDelete = (id: string) => {
    if (window.confirm("Are you sure you want to delete this vendor?")) {
      setVendors(vendors.filter(v => v.id !== id));
    }
  };

  const updateField = (field: keyof Vendor, value: any) => {
    if (editingVendor) {
      setEditingVendor({ ...editingVendor, [field]: value });
    }
  };

  const addSkill = (skill: string) => {
    if (editingVendor && skill.trim()) {
      setEditingVendor({
        ...editingVendor,
        skills: [...editingVendor.skills, skill.trim()]
      });
    }
  };

  const removeSkill = (index: number) => {
    if (editingVendor) {
      setEditingVendor({
        ...editingVendor,
        skills: editingVendor.skills.filter((_, i) => i !== index)
      });
    }
  };

  const addBusinessModel = (model: string) => {
    if (editingVendor && model.trim()) {
      setEditingVendor({
        ...editingVendor,
        businessModels: [...editingVendor.businessModels, model.trim()]
      });
    }
  };

  const removeBusinessModel = (index: number) => {
    if (editingVendor) {
      setEditingVendor({
        ...editingVendor,
        businessModels: editingVendor.businessModels.filter((_, i) => i !== index)
      });
    }
  };

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <div className={styles.headerLeft}>
          <h1 className={styles.title}>Vendors</h1>
        </div>
        <div className={styles.headerRight}>
          <button className={styles.createBtn} onClick={handleCreate}>
            + Create
          </button>
        </div>
      </div>

      <div className={styles.searchBar}>
        <input
          type="text"
          placeholder="Search by name, skills, and more..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className={styles.searchInput}
        />
      </div>

      <div className={styles.tableContainer}>
        <table className={styles.table}>
          <thead>
            <tr>
              <th>NAME</th>
              <th>SKILLS</th>
              <th>BUSINESS MODELS</th>
              <th>RIDE ASSIGNMENT</th>
              <th>ASSIGNED DRIVERS</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {filteredVendors.map((vendor) => (
              <tr key={vendor.id}>
                <td className={styles.vendorName}>{vendor.name}</td>
                <td>
                  <div className={styles.skillsList}>
                    {vendor.skills.length > 0 ? vendor.skills.join(", ") : "-"}
                  </div>
                </td>
                <td>
                  <div className={styles.modelsList}>
                    {vendor.businessModels.join(", ")}
                  </div>
                </td>
                <td>{vendor.rideAssignment}</td>
                <td>{vendor.assignedDrivers}</td>
                <td>
                  <div className={styles.actions}>
                    <button 
                      className={styles.actionBtn} 
                      title="Edit"
                      onClick={() => handleEdit(vendor)}
                    >
                      Edit
                    </button>
                    <button 
                      className={styles.actionBtn} 
                      title="Delete"
                      onClick={() => handleDelete(vendor.id)}
                    >
                      Delete
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className={styles.footer}>
        <span className={styles.resultsCount}>
          Showing {filteredVendors.length} of {vendors.length} vendors
        </span>
      </div>

      {showDrawer && (
        <>
          <div className={styles.drawerOverlay} onClick={handleCloseDrawer} />
          <div className={styles.drawer}>
            <div className={styles.drawerHeader}>
              <h2 className={styles.drawerTitle}>
                {isCreating ? "Add Vendor" : "Edit Vendor"}
              </h2>
              <button className={styles.closeBtn} onClick={handleCloseDrawer}>
                &times;
              </button>
            </div>

            <div className={styles.drawerContent}>
              {editingVendor && (
                <form className={styles.form}>
                  <div className={styles.formGroup}>
                    <label className={styles.label}>Vendor Name</label>
                    <input
                      type="text"
                      className={styles.input}
                      placeholder="Example: Ridebase"
                      value={editingVendor.name}
                      onChange={(e) => updateField("name", e.target.value)}
                    />
                  </div>

                  <div className={styles.formGroup}>
                    <label className={styles.label}>Skills</label>
                    <div className={styles.tagContainer}>
                      {editingVendor.skills.map((skill, index) => (
                        <div key={index} className={styles.tag}>
                          {skill}
                          <button
                            type="button"
                            className={styles.tagRemove}
                            onClick={() => removeSkill(index)}
                          >
                            &times;
                          </button>
                        </div>
                      ))}
                    </div>
                    <div className={styles.tagInput}>
                      <input
                        type="text"
                        className={styles.input}
                        placeholder="Add skill and press Enter"
                        onKeyPress={(e) => {
                          if (e.key === "Enter") {
                            e.preventDefault();
                            addSkill(e.currentTarget.value);
                            e.currentTarget.value = "";
                          }
                        }}
                      />
                    </div>
                  </div>

                  <div className={styles.formGroup}>
                    <label className={styles.label}>Business Models</label>
                    <div className={styles.tagContainer}>
                      {editingVendor.businessModels.map((model, index) => (
                        <div key={index} className={styles.tag}>
                          {model}
                          <button
                            type="button"
                            className={styles.tagRemove}
                            onClick={() => removeBusinessModel(index)}
                          >
                            &times;
                          </button>
                        </div>
                      ))}
                    </div>
                    <div className={styles.tagInput}>
                      <input
                        type="text"
                        className={styles.input}
                        placeholder="Add business model and press Enter"
                        onKeyPress={(e) => {
                          if (e.key === "Enter") {
                            e.preventDefault();
                            addBusinessModel(e.currentTarget.value);
                            e.currentTarget.value = "";
                          }
                        }}
                      />
                    </div>
                  </div>

                  <div className={styles.formRow}>
                    <div className={styles.formGroup}>
                      <label className={styles.label}>Ride Assignment</label>
                      <select
                        className={styles.select}
                        value={editingVendor.rideAssignment}
                        onChange={(e) => updateField("rideAssignment", e.target.value)}
                      >
                        <option value="Manual">Manual</option>
                        <option value="Auto">Auto</option>
                      </select>
                    </div>

                    <div className={styles.formGroup}>
                      <label className={styles.label}>Status</label>
                      <select
                        className={styles.select}
                        value={editingVendor.status}
                        onChange={(e) => updateField("status", e.target.value)}
                      >
                        <option value="Active">Active</option>
                        <option value="Inactive">Inactive</option>
                      </select>
                    </div>
                  </div>

                  <div className={styles.formGroup}>
                    <label className={styles.label}>Contact Email (Optional)</label>
                    <input
                      type="email"
                      className={styles.input}
                      placeholder="vendor@example.com"
                      value={editingVendor.contactEmail || ""}
                      onChange={(e) => updateField("contactEmail", e.target.value)}
                    />
                  </div>

                  <div className={styles.formGroup}>
                    <label className={styles.label}>Contact Phone (Optional)</label>
                    <input
                      type="tel"
                      className={styles.input}
                      placeholder="+1 (555) 123-4567"
                      value={editingVendor.contactPhone || ""}
                      onChange={(e) => updateField("contactPhone", e.target.value)}
                    />
                  </div>

                  <div className={styles.formGroup}>
                    <label className={styles.label}>Assigned Drivers</label>
                    <input
                      type="number"
                      className={styles.input}
                      value={editingVendor.assignedDrivers}
                      onChange={(e) => updateField("assignedDrivers", parseInt(e.target.value) || 0)}
                      disabled
                    />
                    <small className={styles.helpText}>This is calculated automatically</small>
                  </div>
                </form>
              )}
            </div>

            <div className={styles.drawerFooter}>
              <button className={styles.cancelBtn} onClick={handleCloseDrawer}>
                Cancel
              </button>
              <button className={styles.saveBtn} onClick={handleSave}>
                {isCreating ? "Add Vendor" : "Save Changes"}
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}